import { searchBookableAssets } from '../api/assetListingApi';
import { getAssetById } from '../api/getAssetById';
import { registeredAssetThumbnailById, registerAssetThumbnailRecords } from './assetThumbnailStore';
import { coalesceAssetListRecord, normalizeAssetListResponse } from './assetListNormalize';
import { mergeAssetApiRecordWithListSnapshot } from './assetRecordMerge';
import { browseAssetListSnapshotForId } from './browseAssetListSnapshot';
import { resolveBookingAssetThumbnailSrc } from './bookingListDisplay';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceAssetId(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/** Normalize UUID-style ids so `abc-def` and `abcdef` match the same catalog row. */
export function normalizeAssetLookupKey(id: string): string {
  return id.trim().toLowerCase().replace(/-/g, '');
}

/** All keys used to index and look up an asset in the thumbnail catalog. */
export function assetRecordLookupKeys(record: Record<string, unknown>): string[] {
  const keys = new Set<string>();
  const add = (raw: unknown) => {
    const id = coerceAssetId(raw);
    if (!id) {
      return;
    }
    keys.add(id.toLowerCase());
    keys.add(normalizeAssetLookupKey(id));
  };
  add(record.id);
  add(record.Id);
  add(record.asset_id);
  add(record.assetId);
  add(record.AssetId);
  return Array.from(keys);
}

function thumbFromAssetRecord(record: Record<string, unknown>): string | null {
  return resolveBookingAssetThumbnailSrc(coalesceAssetListRecord(record));
}

function indexCatalogEntry(
  catalog: Map<string, string | null>,
  record: Record<string, unknown>,
  src: string | null,
): void {
  if (!src) {
    return;
  }
  for (const key of assetRecordLookupKeys(record)) {
    // Always overwrite — owner image updates must replace the first cached thumb.
    catalog.set(key, src);
  }
}

export function catalogThumbForAssetId(
  catalog: ReadonlyMap<string, string | null>,
  assetId: string,
): string | null {
  for (const key of [assetId.toLowerCase(), normalizeAssetLookupKey(assetId)]) {
    const hit = catalog.get(key);
    if (hit) {
      return hit;
    }
  }
  return null;
}

let searchCatalogPromise: Promise<Map<string, string | null>> | null = null;

async function loadSearchThumbnailCatalog(): Promise<Map<string, string | null>> {
  const catalog = new Map<string, string | null>();
  try {
    const searchRaw = await searchBookableAssets();
    const assets = normalizeAssetListResponse(searchRaw);
    registerAssetThumbnailRecords(assets);
    for (const asset of assets) {
      indexCatalogEntry(catalog, asset, thumbFromAssetRecord(asset));
    }
  } catch {
    /* search is best-effort */
  }
  return catalog;
}

function getSearchThumbnailCatalog(): Promise<Map<string, string | null>> {
  if (!searchCatalogPromise) {
    searchCatalogPromise = loadSearchThumbnailCatalog();
  }
  return searchCatalogPromise;
}

/** Clears cached search thumbnails (also after owner image updates). */
export function invalidateBookingAssetThumbnailSearchCache(): void {
  searchCatalogPromise = null;
}

/** @deprecated Prefer {@link invalidateBookingAssetThumbnailSearchCache}. */
export function resetBookingAssetThumbnailCacheForTests(): void {
  invalidateBookingAssetThumbnailSearchCache();
}

/**
 * Resolve a booking-list thumbnail — same `assetRecordFirstImageSrc` path as home/browse,
 * backed by the search catalog with per-id GET fallback.
 */
export async function resolveBookingAssetThumbnailById(
  assetId: string,
  options?: { forceRefresh?: boolean; listSnapshot?: Record<string, unknown> },
): Promise<string | null> {
  const listSnapshot = options?.listSnapshot ?? browseAssetListSnapshotForId(assetId);
  if (!options?.forceRefresh) {
    const fromStore = registeredAssetThumbnailById(assetId);
    if (fromStore) {
      return fromStore;
    }
    const catalog = await getSearchThumbnailCatalog();
    const fromCatalog = catalogThumbForAssetId(catalog, assetId);
    if (fromCatalog) {
      return fromCatalog;
    }
  }
  try {
    const payload = await getAssetById(assetId);
    if (!isPlainObject(payload)) {
      return null;
    }
    let coalesced = coalesceAssetListRecord(payload);
    if (listSnapshot) {
      coalesced = mergeAssetApiRecordWithListSnapshot(coalesced, listSnapshot);
    }
    const src = resolveBookingAssetThumbnailSrc(coalesced);
    if (src) {
      registerAssetThumbnailRecords([coalesced]);
      const catalog = await getSearchThumbnailCatalog();
      indexCatalogEntry(catalog, coalesced, src);
      for (const key of assetRecordLookupKeys({ asset_id: assetId })) {
        if (!catalog.has(key)) {
          catalog.set(key, src);
        }
      }
    }
    return src;
  } catch {
    if (listSnapshot) {
      const fromSnapshot = resolveBookingAssetThumbnailSrc(coalesceAssetListRecord(listSnapshot));
      if (fromSnapshot) {
        registerAssetThumbnailRecords([coalesceAssetListRecord(listSnapshot)]);
        return fromSnapshot;
      }
    }
    return null;
  }
}

export async function loadBookingListAssetThumbnails(
  assetIds: readonly string[],
  options?: {
    forceRefresh?: boolean;
    listSnapshots?: ReadonlyMap<string, Record<string, unknown>>;
    /** Limit parallel GET-by-id calls (default: unbounded Promise.all). */
    concurrency?: number;
  },
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  const unique = Array.from(
    new Set(assetIds.map((id) => id.trim()).filter(Boolean)),
  );
  if (unique.length === 0) {
    return result;
  }

  const concurrency =
    typeof options?.concurrency === 'number' && Number.isFinite(options.concurrency)
      ? Math.max(1, Math.floor(options.concurrency))
      : unique.length;

  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(concurrency, unique.length) }, async () => {
    while (nextIndex < unique.length) {
      const index = nextIndex;
      nextIndex += 1;
      const assetId = unique[index];
      const listSnapshot =
        options?.listSnapshots?.get(assetId) ??
        options?.listSnapshots?.get(assetId.toLowerCase()) ??
        browseAssetListSnapshotForId(assetId);
      const src = await resolveBookingAssetThumbnailById(assetId, {
        forceRefresh: options?.forceRefresh,
        listSnapshot: listSnapshot ?? undefined,
      });
      const keys = [assetId.toLowerCase(), normalizeAssetLookupKey(assetId)];
      for (const key of keys) {
        result.set(key, src);
      }
    }
  });
  await Promise.all(workers);

  return result;
}
